import { useEffect, useLayoutEffect } from 'react';

// Column resizing for every shared table (V2-027; DESIGN.md TBL-18).
//
// Drag the divider at the right edge of a header cell to widen or narrow its column;
// double-click the divider to fit the column to its content again. Widths are remembered for
// the sign-in per table (sessionStorage, `wrap.` prefix, so they are cleared at sign-out with the
// rest of the user's state – IMP-07).
//
// Built into <Table>, so DataTable, the HE entry grid and every hand-written list table get it
// without per-page code. Nothing is injected into the header cells: the divider is a CSS
// ::after on the cell (tailwind.css `.wrap-resizable`), and a press within its width starts the
// drag. On a coarse pointer the hit area is wider without a wider visible handle (TCH-05).
// Tables that bring their own resize handles (the task tables) are left alone.

const MIN_WIDTH_PX = 64;
const EDGE_PX_FINE = 8;
const EDGE_PX_COARSE = 22;
const STORAGE_PREFIX = 'wrap.columns:';

function headerCells(table: HTMLTableElement): HTMLTableCellElement[] {
  const row = table.tHead?.rows[table.tHead.rows.length - 1];
  return row ? (Array.from(row.cells) as HTMLTableCellElement[]) : [];
}

function headerKey(th: HTMLTableCellElement, index: number): string {
  return `${index}:${(th.textContent ?? '').trim()}`;
}

function storageKey(table: HTMLTableElement): string {
  const signature = headerCells(table)
    .map((th) => (th.textContent ?? '').trim())
    .join('|');
  return `${STORAGE_PREFIX}${window.location.pathname}:${signature}`;
}

function readWidths(key: string): Record<string, number> {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeWidths(key: string, widths: Record<string, number>): void {
  try {
    if (Object.keys(widths).length === 0) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(widths));
  } catch {
    // Storage unavailable (private mode): resizing still works for this view.
  }
}

function setWidth(th: HTMLTableCellElement, px: number | null): void {
  const value = px == null ? '' : `${px}px`;
  th.style.width = value;
  th.style.minWidth = value;
  th.style.maxWidth = value;
}

function ownsResizeHandles(table: HTMLTableElement): boolean {
  return table.querySelector('thead .resize-handle') != null;
}

/** Small fixed tables (dialogs, roughly four columns or fewer) are not resizable (TBL-18). */
function isResizable(table: HTMLTableElement): boolean {
  return headerCells(table).length > 4 && !ownsResizeHandles(table);
}

export function useTableColumnResize(
  tableRef: React.RefObject<HTMLTableElement>,
  enabled: boolean,
): void {
  // Re-apply remembered widths after every render (headers can mount after the data arrives).
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!enabled || !table || !isResizable(table)) return;
    table.classList.add('wrap-resizable');
    const saved = readWidths(storageKey(table));
    headerCells(table).forEach((th, i) => {
      const w = saved[headerKey(th, i)];
      if (typeof w === 'number' && th.style.width !== `${w}px`) setWidth(th, w);
    });
  });

  useEffect(() => {
    const table = tableRef.current;
    if (!enabled || !table) return;

    let drag: { th: HTMLTableCellElement; index: number; startX: number; startWidth: number; moved: boolean } | null = null;
    let suppressClick = false;

    const edgeHit = (e: PointerEvent | MouseEvent, th: HTMLTableCellElement) => {
      const coarse = 'pointerType' in e && (e as PointerEvent).pointerType !== 'mouse';
      const zone = coarse ? EDGE_PX_COARSE : EDGE_PX_FINE;
      return e.clientX >= th.getBoundingClientRect().right - zone;
    };
    const headerCellAt = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return null;
      const th = target.closest('th') as HTMLTableCellElement | null;
      if (!th || th.closest('table') !== table || !th.closest('thead')) return null;
      return th;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!isResizable(table) || e.button !== 0) return;
      const th = headerCellAt(e.target);
      if (!th || !edgeHit(e, th)) return;
      e.preventDefault();
      const index = headerCells(table).indexOf(th);
      drag = { th, index, startX: e.clientX, startWidth: th.getBoundingClientRect().width, moved: false };
      document.body.style.cursor = 'col-resize';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) > 2) drag.moved = true;
      setWidth(drag.th, Math.max(MIN_WIDTH_PX, Math.round(drag.startWidth + dx)));
    };
    const onPointerUp = () => {
      if (!drag) return;
      document.body.style.cursor = '';
      if (drag.moved) {
        suppressClick = true; // the release must not also sort the column
        const key = storageKey(table);
        const widths = readWidths(key);
        widths[headerKey(drag.th, drag.index)] = Math.round(drag.th.getBoundingClientRect().width);
        writeWidths(key, widths);
      }
      drag = null;
    };
    const onClickCapture = (e: MouseEvent) => {
      if (!suppressClick) return;
      suppressClick = false;
      e.stopPropagation();
      e.preventDefault();
    };
    const onDoubleClick = (e: MouseEvent) => {
      if (!isResizable(table)) return;
      const th = headerCellAt(e.target);
      if (!th || !edgeHit(e, th)) return;
      // Fit to content: drop the explicit width and forget it.
      setWidth(th, null);
      const key = storageKey(table);
      const widths = readWidths(key);
      delete widths[headerKey(th, headerCells(table).indexOf(th))];
      writeWidths(key, widths);
    };

    table.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    table.addEventListener('click', onClickCapture, true);
    table.addEventListener('dblclick', onDoubleClick);
    return () => {
      table.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      table.removeEventListener('click', onClickCapture, true);
      table.removeEventListener('dblclick', onDoubleClick);
    };
  }, [tableRef, enabled]);
}

/** Forget every remembered column width on this page ("Reset Columns"). */
export function resetColumnWidths(pathname: string = window.location.pathname): void {
  try {
    const prefix = `${STORAGE_PREFIX}${pathname}:`;
    for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(prefix)) window.sessionStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}
